import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  Brain, Plus, Trash2, Lightbulb, 
  CheckCircle2, Sparkles, Loader2, Send, X, Check, Mic, Users, AlertTriangle, Calendar,
  ListTodo, MessageSquare, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section 3A Fix: Exactly 5 categories as per spec
const categories = [
  { value: 'task', label: 'Task', icon: CheckCircle2, color: 'bg-blue-100 text-blue-600', canConvert: ['task'] },
  { value: 'calendar', label: 'Calendar', icon: Calendar, color: 'bg-purple-100 text-purple-600', canConvert: ['calendar'] },
  { value: 'worry', label: 'Worry', icon: AlertTriangle, color: 'bg-amber-100 text-amber-600', canConvert: [] },
  { value: 'idea', label: 'Idea', icon: Lightbulb, color: 'bg-yellow-100 text-yellow-600', canConvert: [] },
  { value: 'convo', label: 'Convo', icon: MessageSquare, color: 'bg-pink-100 text-pink-600', canConvert: ['task', 'handoff_topic'] },
];

const forWhomOptions = [
  { value: 'asisten', label: 'Asisten' },
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'grandparent', label: 'Grandparent' },
];

const BrainDumpPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickInput, setQuickInput] = useState('');
  const [quickCategory, setQuickCategory] = useState('task'); // Default to 'task' now
  
  // E3-A: AI Process State
  const [processing, setProcessing] = useState(false);
  const [sortedSuggestions, setSortedSuggestions] = useState([]);
  const [processCallId, setProcessCallId] = useState(null);
  
  // E3-C: Handoff Writer State
  const [selectedItems, setSelectedItems] = useState([]);
  const [writingHandoff, setWritingHandoff] = useState(false);
  const [generatedHandoff, setGeneratedHandoff] = useState(null);
  const [handoffCallId, setHandoffCallId] = useState(null);
  const [handoffForWhom, setHandoffForWhom] = useState('asisten');
  
  // E3-B: Voice Input State (placeholder)
  const [isRecording, setIsRecording] = useState(false);
  
  // Phase 1: Conversion dialog state
  const [convertDialog, setConvertDialog] = useState({ open: false, item: null });
  const [convertDate, setConvertDate] = useState('');
  const [convertTime, setConvertTime] = useState('09:00');
  const [converting, setConverting] = useState(false);
  const [parsingDate, setParsingDate] = useState(false);
  const [parsedEventTitle, setParsedEventTitle] = useState('');
  
  // Bug 3 Fix: Handoff dialog state
  const [handoffDialog, setHandoffDialog] = useState({ open: false, item: null });
  const [handoffAssignee, setHandoffAssignee] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);

  useEffect(() => {
    fetchItems();
    fetchFamilyMembers();
  }, []);

  // Bug 3 Fix: Fetch family members for handoff assignee
  const fetchFamilyMembers = async () => {
    try {
      const response = await axios.get(`${API}/family/roles`);
      const members = response.data?.members?.map(m => ({ name: m.name, role: m.role })) || [];
      setFamilyMembers(members);
    } catch (error) {
      console.error('Error fetching family members:', error);
    }
  };

  // HITL confirmation helper
  const confirmAIAction = async (callId, userAction, editDelta = []) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/brain-dump`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching brain dump:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickInput.trim()) return;
    
    try {
      await axios.post(`${API}/brain-dump`, {
        content: quickInput,
        category: quickCategory
      });
      toast.success('Captured!');
      setQuickInput('');
      fetchItems();
    } catch (error) {
      toast.error('Failed to capture thought');
    }
  };

  const markProcessed = async (item) => {
    try {
      await axios.put(`${API}/brain-dump/${item.id}`, { ...item, is_processed: true });
      toast.success('Marked as processed');
      fetchItems();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/brain-dump/${id}`);
      toast.success('Removed');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // E3-A: AI Batch Process
  const handleAIProcess = async () => {
    const unprocessedItems = items.filter(i => !i.is_processed);
    if (unprocessedItems.length === 0) {
      toast.info('No unprocessed items to sort');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/ai/brain-dump/process`, {
        items: unprocessedItems
      });
      setSortedSuggestions(response.data.sorted_items || []);
      setProcessCallId(response.data.call_id);
    } catch (error) {
      toast.error('AI processing failed');
    } finally {
      setProcessing(false);
    }
  };

  // E3-A: Apply AI suggestions (with compound split handling)
  // E4B Fix: Added calendar conversion
  const applySuggestion = async (suggestion, originalItem) => {
    // For split items, we need to create new brain dump items
    if (suggestion.was_split && suggestion.parent_index !== null) {
      // Create new brain dump item from split
      try {
        await axios.post(`${API}/brain-dump`, {
          content: suggestion.content,
          category: suggestion.suggested_category
        });
        toast.success(`Created new item: ${suggestion.content.substring(0, 30)}...`);
        
        // Remove from suggestions
        setSortedSuggestions(prev => prev.filter(s => s.content !== suggestion.content));
        
        // If last suggestion, confirm AI action
        if (sortedSuggestions.length === 1) {
          await confirmAIAction(processCallId, 'accepted');
          setProcessCallId(null);
        }
        
        fetchItems();
      } catch (error) {
        toast.error('Failed to create split item');
      }
      return;
    }
    
    // Normal reclassification for non-split items
    if (!originalItem) return;
    
    try {
      await axios.post(`${API}/ai/brain-dump/reclassify/${originalItem.id}?new_category=${suggestion.suggested_category}`);
      toast.success(`Reclassified to ${suggestion.suggested_category}`);
      
      // Remove from suggestions
      setSortedSuggestions(prev => prev.filter(s => s.content !== suggestion.content));
      
      // If last suggestion, confirm AI action
      if (sortedSuggestions.length === 1) {
        await confirmAIAction(processCallId, 'accepted');
        setProcessCallId(null);
      }
      
      fetchItems();
    } catch (error) {
      toast.error('Failed to reclassify');
    }
  };

  // E4B Fix: Convert brain dump item to calendar event
  const convertToCalendar = async (item) => {
    try {
      // Create calendar event from brain dump item
      const response = await axios.post(`${API}/ai/brain-dump/to-calendar`, {
        brain_dump_id: item.id,
        content: item.content
      });
      
      if (response.data.event) {
        toast.success('Created calendar event!', {
          description: response.data.event.title
        });
        // Delete or mark brain dump item as converted
        await axios.delete(`${API}/brain-dump/${item.id}`);
        fetchItems();
      } else {
        toast.error('Could not extract date/time from item');
      }
    } catch (error) {
      toast.error('Failed to convert to calendar');
    }
  };

  // E3-A: Dismiss AI suggestions
  const dismissSuggestions = async () => {
    await confirmAIAction(processCallId, 'ignored');
    setSortedSuggestions([]);
    setProcessCallId(null);
  };

  // Phase 1 Step 1 & 2: Convert brain dump to task/calendar/handoff
  const handleConvert = async (item, target) => {
    // For calendar, need date - call AI to parse then show dialog
    if (target === 'calendar') {
      setConvertDialog({ open: true, item, target: 'calendar' });
      setConvertDate('');
      setConvertTime('09:00');
      setParsedEventTitle('');
      
      // Priority 2 Fix: Call AI to extract date/time from the content
      setParsingDate(true);
      try {
        const response = await axios.post(`${API}/ai/brain-dump/to-calendar`, {
          content: item.content,
          brain_dump_id: item.id
        });
        
        if (response.data.event) {
          // Pre-fill the form with AI-extracted values
          setConvertDate(response.data.event.date || '');
          setConvertTime(response.data.event.time || '09:00');
          setParsedEventTitle(response.data.event.title || '');
        }
      } catch (error) {
        console.log('Could not auto-parse date:', error);
        // Silent fail - user can still enter manually
      } finally {
        setParsingDate(false);
      }
      return;
    }
    
    // Bug 3 Fix: For handoff_topic, show dialog to select assignee
    if (target === 'handoff_topic') {
      setHandoffDialog({ open: true, item });
      setHandoffAssignee('');
      return;
    }
    
    // For task, convert directly
    setConverting(true);
    try {
      await axios.post(`${API}/braindump/convert/${item.id}`, { target });
      toast.success('Converted to Task!');
      fetchItems();
    } catch (error) {
      toast.error('Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  // Bug 3 Fix: Handle handoff conversion with assignee
  const handleHandoffConvert = async () => {
    if (!handoffDialog.item) return;
    
    setConverting(true);
    try {
      await axios.post(`${API}/braindump/convert/${handoffDialog.item.id}`, {
        target: 'handoff_topic',
        assignee: handoffAssignee || null
      });
      toast.success(handoffAssignee ? `Handoff to ${handoffAssignee}!` : 'Added to Handoff Board!');
      setHandoffDialog({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error('Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  const handleCalendarConvert = async () => {
    if (!convertDate) {
      toast.error('Please select a date');
      return;
    }
    
    setConverting(true);
    try {
      await axios.post(`${API}/braindump/convert/${convertDialog.item.id}`, {
        target: 'calendar',
        date: convertDate,
        time: convertTime
      });
      toast.success('Added to Calendar!');
      setConvertDialog({ open: false, item: null });
      setConvertDate('');
      fetchItems();
    } catch (error) {
      toast.error('Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  // E3-A: Change category manually
  const handleCategoryChange = async (itemId, newCategory) => {
    try {
      await axios.post(`${API}/ai/brain-dump/reclassify/${itemId}?new_category=${newCategory}`);
      toast.success('Category updated');
      fetchItems();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  // E3-C: Toggle item selection for handoff
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // E3-C: AI Write Handoff
  const handleWriteHandoff = async () => {
    if (selectedItems.length === 0) {
      toast.info('Select items to create handoff from');
      return;
    }
    
    const selectedData = items.filter(i => selectedItems.includes(i.id));
    
    setWritingHandoff(true);
    try {
      const response = await axios.post(`${API}/ai/handoff/write`, {
        brain_dump_items: selectedData,
        for_whom: handoffForWhom
      });
      setGeneratedHandoff(response.data.handoff);
      setHandoffCallId(response.data.call_id);
    } catch (error) {
      toast.error('Failed to generate handoff');
    } finally {
      setWritingHandoff(false);
    }
  };

  // E3-C: Signal 2 - Save handoff to board
  const saveHandoff = async () => {
    if (!generatedHandoff) return;
    
    try {
      await axios.post(`${API}/handoff`, {
        what: generatedHandoff.what,
        when: generatedHandoff.when || '',
        how: generatedHandoff.how || '',
        special_notes: generatedHandoff.special_notes || '',
        for_whom: generatedHandoff.for_whom,
        priority: 'normal'
      });
      
      // Mark selected brain dump items as processed
      for (const itemId of selectedItems) {
        const item = items.find(i => i.id === itemId);
        if (item) {
          await axios.put(`${API}/brain-dump/${itemId}`, { ...item, is_processed: true });
        }
      }
      
      toast.success('Handoff created and items processed!');
      await confirmAIAction(handoffCallId, 'accepted');
      
      setGeneratedHandoff(null);
      setHandoffCallId(null);
      setSelectedItems([]);
      fetchItems();
      
      // Navigate to handoff board
      navigate('/handoff');
    } catch (error) {
      toast.error('Failed to save handoff');
    }
  };

  // E3-C: Signal 2 - Edit and save
  const editAndSaveHandoff = () => {
    // Navigate to handoff with pre-filled data
    navigate('/handoff', { state: { prefill: generatedHandoff } });
    confirmAIAction(handoffCallId, 'edited');
    setGeneratedHandoff(null);
    setHandoffCallId(null);
  };

  // E3-C: Signal 2 - Discard
  const discardHandoff = async () => {
    await confirmAIAction(handoffCallId, 'ignored');
    setGeneratedHandoff(null);
    setHandoffCallId(null);
    toast.info('Handoff discarded');
  };

  // E3-B: Voice input placeholder
  const handleVoiceInput = () => {
    toast.info('Voice input coming soon!', { description: 'Seed Speech integration planned' });
    setIsRecording(!isRecording);
    setTimeout(() => setIsRecording(false), 2000);
  };

  const unprocessedItems = items.filter(i => !i.is_processed);
  const processedItems = items.filter(i => i.is_processed);

  const getCategoryInfo = (value) => categories.find(c => c.value === value) || categories[0];

  return (
    <div className="space-y-6" data-testid="brain-dump-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            Brain Dump
          </h1>
          <p className="text-nurture-muted mt-1">Quick capture your thoughts - sort later</p>
        </div>
        
        {/* E3-A: AI Process Button */}
        <div className="flex gap-2">
          <Button
            onClick={handleAIProcess}
            disabled={processing || unprocessedItems.length === 0}
            className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover"
            data-testid="ai-process-btn"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            AI Sort All
          </Button>
        </div>
      </div>

      {/* Quick Capture */}
      <Card className="shadow-nurture">
        <CardContent className="p-4">
          <form onSubmit={handleQuickAdd} className="space-y-3">
            {/* Input row - full width on mobile */}
            <div className="flex gap-2">
              <Input
                value={quickInput}
                onChange={(e) => setQuickInput(e.target.value)}
                placeholder="What's on your mind? Just dump it here..."
                className="flex-1 rounded-xl text-base"
                data-testid="quick-input"
              />
              {/* E3-B: Voice Input Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleVoiceInput}
                className={`rounded-xl flex-shrink-0 ${isRecording ? 'bg-red-100 border-red-300' : ''}`}
                data-testid="voice-input-btn"
              >
                <Mic className={`w-4 h-4 ${isRecording ? 'text-red-500 animate-pulse' : ''}`} />
              </Button>
            </div>
            {/* Category + Add button row */}
            <div className="flex gap-2">
              <Select value={quickCategory} onValueChange={setQuickCategory}>
                <SelectTrigger className="flex-1 sm:w-36 sm:flex-none rounded-xl" data-testid="quick-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" className="flex-1 sm:flex-none rounded-xl bg-indigo-500 hover:bg-indigo-600" data-testid="quick-add-btn">
                <Plus className="w-4 h-4 mr-2 sm:mr-0" />
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* AI Sorted Suggestions */}
      {sortedSuggestions.length > 0 && (
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-none shadow-nurture">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-nurture-terracotta" />
                AI Sorting Suggestions
              </CardTitle>
              <Button variant="outline" size="sm" onClick={dismissSuggestions}>
                <X className="w-4 h-4 mr-1" /> Dismiss All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {sortedSuggestions.map((suggestion, index) => {
              const catInfo = getCategoryInfo(suggestion.suggested_category);
              const originalItem = suggestion.was_split ? null : items.find(i => i.content === suggestion.content);
              const Icon = catInfo.icon;
              
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-xl">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-8 h-8 rounded-lg ${catInfo.color} flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-nurture-navy">{suggestion.content}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">→ {catInfo.label}</Badge>
                        {/* Section 3B: Show split badge */}
                        {suggestion.was_split && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs" data-testid="split-badge">
                            Split from 1 entry
                          </Badge>
                        )}
                        {suggestion.is_signal_4 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid="signal-4-badge">
                            Signal 4: Can delegate
                          </Badge>
                        )}
                        {/* E4B Fix: Calendar conversion hint */}
                        {suggestion.suggested_category === 'calendar' && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            → Calendar event
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {/* E4B Fix: Direct convert to calendar for calendar items */}
                    {suggestion.suggested_category === 'calendar' && originalItem && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => convertToCalendar(originalItem)}
                        className="text-purple-500 border-purple-200"
                        title="Convert to Calendar"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => applySuggestion(suggestion, originalItem)}
                      className="bg-nurture-sage hover:bg-nurture-sage-hover"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* E3-C: Handoff Writer Section */}
      {selectedItems.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-none shadow-nurture">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-500 text-white">{selectedItems.length} selected</Badge>
                <span className="text-sm text-nurture-muted">Create handoff from these items</span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={handoffForWhom} onValueChange={setHandoffForWhom}>
                  <SelectTrigger className="w-36 rounded-xl" data-testid="handoff-for-whom">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {forWhomOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleWriteHandoff}
                  disabled={writingHandoff}
                  className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover"
                  data-testid="write-handoff-btn"
                >
                  {writingHandoff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  AI Write Handoff
                </Button>
                <Button variant="outline" onClick={() => setSelectedItems([])}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* E3-C: Generated Handoff Preview (Signal 2) */}
      {generatedHandoff && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-nurture">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-nurture-sage" />
              Generated Handoff for {handoffForWhom}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-4 bg-white rounded-xl space-y-2">
              <div>
                <span className="text-xs text-nurture-muted uppercase">What</span>
                <p className="font-medium text-nurture-navy">{generatedHandoff.what}</p>
              </div>
              {generatedHandoff.when && (
                <div>
                  <span className="text-xs text-nurture-muted uppercase">When</span>
                  <p className="text-nurture-navy">{generatedHandoff.when}</p>
                </div>
              )}
              {generatedHandoff.how && (
                <div>
                  <span className="text-xs text-nurture-muted uppercase">How</span>
                  <p className="text-nurture-navy">{generatedHandoff.how}</p>
                </div>
              )}
              {generatedHandoff.special_notes && (
                <div>
                  <span className="text-xs text-nurture-muted uppercase">Notes</span>
                  <p className="text-amber-600">{generatedHandoff.special_notes}</p>
                </div>
              )}
            </div>
            
            {/* Signal 2: Three outcome buttons */}
            <div className="flex gap-2 pt-2">
              <Button onClick={saveHandoff} className="bg-nurture-sage hover:bg-nurture-sage-hover" data-testid="save-handoff-btn">
                <Check className="w-4 h-4 mr-2" /> Save to Handoff Board
              </Button>
              <Button variant="outline" onClick={editAndSaveHandoff} data-testid="edit-handoff-btn">
                Edit & Save
              </Button>
              <Button variant="outline" onClick={discardHandoff} data-testid="discard-handoff-btn">
                <X className="w-4 h-4 mr-2" /> Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unprocessed Items */}
      <Card className="shadow-nurture">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Unprocessed ({unprocessedItems.length})</span>
            {unprocessedItems.length > 0 && (
              <span className="text-sm font-normal text-nurture-muted">
                Use → buttons to convert items
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-nurture-muted py-8">Loading...</p>
          ) : unprocessedItems.length === 0 ? (
            <p className="text-center text-nurture-muted py-8">Your mind is clear! Add something above.</p>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {unprocessedItems.map((item) => {
                  const catInfo = getCategoryInfo(item.category);
                  const Icon = catInfo.icon;
                  const isSelected = selectedItems.includes(item.id);
                  
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                        isSelected ? 'border-blue-300 bg-blue-50' : 'border-nurture-cream-dark bg-white'
                      }`}
                    >
                      {/* E3-C: Selection checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleItemSelection(item.id)}
                        className="mt-1"
                        data-testid={`select-item-${item.id}`}
                      />
                      
                      <div className={`w-10 h-10 rounded-xl ${catInfo.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-nurture-navy">{item.content}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {/* E3-A: Category dropdown */}
                          <Select 
                            value={item.category} 
                            onValueChange={(val) => handleCategoryChange(item.id, val)}
                          >
                            <SelectTrigger className="w-32 h-7 text-xs rounded-lg" data-testid={`category-${item.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="flex gap-1 flex-wrap">
                        {/* Phase 1: Convert buttons based on category - Bug 2: Added text labels */}
                        {item.category === 'task' && !item.converted && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleConvert(item, 'task')}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs px-2"
                            data-testid={`to-task-${item.id}`}
                          >
                            <ListTodo className="w-3 h-3 mr-1" />
                            → task
                          </Button>
                        )}
                        {item.category === 'calendar' && !item.converted && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleConvert(item, 'calendar')}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 text-xs px-2"
                            data-testid={`to-calendar-${item.id}`}
                          >
                            <Calendar className="w-3 h-3 mr-1" />
                            → calendar
                          </Button>
                        )}
                        {item.category === 'convo' && !item.converted && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleConvert(item, 'task')}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs px-2"
                            >
                              <ListTodo className="w-3 h-3 mr-1" />
                              → task
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleConvert(item, 'handoff_topic')}
                              className="text-pink-600 hover:text-pink-700 hover:bg-pink-50 text-xs px-2"
                            >
                              <ArrowRight className="w-3 h-3 mr-1" />
                              → handoff
                            </Button>
                          </>
                        )}
                        {item.converted && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            → {item.converted_to}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markProcessed(item)}
                          className="text-nurture-sage hover:text-nurture-sage-hover"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem(item.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processed Items */}
      {processedItems.length > 0 && (
        <Card className="shadow-nurture opacity-75">
          <CardHeader>
            <CardTitle className="text-nurture-muted">Processed ({processedItems.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {processedItems.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-3 rounded-lg bg-nurture-cream-dark/50">
                  <CheckCircle2 className="w-4 h-4 text-nurture-sage" />
                  <span className="text-nurture-muted line-through flex-1">{item.content}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteItem(item.id)}
                    className="text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {processedItems.length > 5 && (
                <p className="text-sm text-nurture-muted text-center">
                  + {processedItems.length - 5} more processed items
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 1: Calendar Conversion Dialog */}
      <Dialog open={convertDialog.open} onOpenChange={(open) => !open && setConvertDialog({ open: false, item: null })}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Add to Calendar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-nurture-muted">
              {convertDialog.item?.content}
            </p>
            {parsingDate && (
              <div className="flex items-center gap-2 text-sm text-purple-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Detecting date/time from text...
              </div>
            )}
            {parsedEventTitle && !parsingDate && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded-xl">
                ✓ Detected: {parsedEventTitle}
              </div>
            )}
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={convertDate}
                onChange={(e) => setConvertDate(e.target.value)}
                className="rounded-xl"
                data-testid="convert-date-input"
                disabled={parsingDate}
              />
            </div>
            <div className="space-y-2">
              <Label>Time (optional)</Label>
              <Input
                type="time"
                value={convertTime}
                onChange={(e) => setConvertTime(e.target.value)}
                className="rounded-xl"
                data-testid="convert-time-input"
                disabled={parsingDate}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCalendarConvert}
                disabled={converting || !convertDate || parsingDate}
                className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-xl"
                data-testid="confirm-calendar-convert"
              >
                {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Calendar'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setConvertDialog({ open: false, item: null })}
                className="rounded-xl"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bug 3 Fix: Handoff Dialog with Assignee Picker */}
      <Dialog open={handoffDialog.open} onOpenChange={(open) => !open && setHandoffDialog({ open: false, item: null })}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-500" />
              Create Handoff
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-nurture-muted bg-pink-50 p-3 rounded-xl">
              {handoffDialog.item?.content}
            </p>
            <div className="space-y-2">
              <Label>Assign to (optional)</Label>
              <Select value={handoffAssignee} onValueChange={setHandoffAssignee}>
                <SelectTrigger className="rounded-xl" data-testid="handoff-assignee-select">
                  <SelectValue placeholder="Select family member..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No specific person</SelectItem>
                  {familyMembers.map((member) => (
                    <SelectItem key={member.name} value={member.name}>
                      {member.name} <span className="text-xs text-nurture-muted ml-1">({member.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-nurture-muted">
                Leave empty to add to general handoff board
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleHandoffConvert}
                disabled={converting}
                className="flex-1 bg-pink-500 hover:bg-pink-600 rounded-xl"
                data-testid="confirm-handoff-convert"
              >
                {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Handoff'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setHandoffDialog({ open: false, item: null })}
                className="rounded-xl"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrainDumpPage;
